/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Box, Grid, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { InfoOutlined } from '@material-ui/icons';
import { observer } from 'mobx-react';
import { AmcOption } from 'libs/feature-documents/src/documents/stores/documentForms/appraisalDetailsV2/amcFilterRules';

const useStyles = makeStyles(theme => ({
  card: {
    cursor: 'pointer',
    border: '1.5px solid #E0E0E0',
    borderRadius: '8px !important',
    padding: '14px 20px',
    minWidth: 200,
    transition: 'border-color 0.15s',
    userSelect: 'none',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  selectedCard: {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  radioIndicator: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: '2px solid #9E9E9E',
    backgroundColor: 'transparent',
    flexShrink: 0,
  },
  radioIndicatorSelected: {
    width: 18,
    height: 18,
    backgroundColor: theme.palette.primary.main,
    boxShadow: '0 0 0 3px white inset',
    border: `2px solid ${theme.palette.primary.main}`,
  },
  estFeeLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#9E9E9E',
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
  },
  amcName: {
    fontWeight: 700,
    fontSize: 16,
    marginTop: 6,
    marginBottom: 4,
  },
  price: {
    fontWeight: 700,
    fontSize: 22,
    lineHeight: 1.2,
  },
  priceCents: {
    fontSize: 14,
    fontWeight: 500,
  },
  disclaimer: {
    marginTop: 16,
    padding: '10px 14px',
    backgroundColor: '#E3F2FD',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  disclaimerIcon: {
    color: theme.palette.primary.main,
    fontSize: '18px !important',
    marginTop: 1,
    flexShrink: 0,
  },
  disclaimerText: {
    color: '#0D47A1',
    fontSize: 12,
    lineHeight: 1.5,
  },
}));

interface Props {
  options: AmcOption[];
  value: string;
  onChange: (value: string) => void;
}

export const AmcSelectionCards = observer(({ options, value, onChange }: Props) => {
  const classes = useStyles();
  const hasFeeOptions = options.some(o => o.estimatedFee > 0);

  return (
    <Box>
      <Grid container spacing={2}>
        {options.map(option => {
          const selected = value === option.value;
          const hasFee = option.estimatedFee > 0;
          const [whole, cents] = hasFee
            ? option.estimatedFee.toFixed(2).split('.')
            : ['', ''];

          return (
            <Grid item key={option.value}>
              <Paper
                className={`${classes.card} ${selected ? classes.selectedCard : ''}`}
                onClick={() => onChange(option.value)}
                elevation={0}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box
                    className={`${classes.radioIndicator} ${selected ? classes.radioIndicatorSelected : ''}`}
                  />
                  {hasFee && (
                    <Typography className={classes.estFeeLabel}>Est. Fee</Typography>
                  )}
                </Box>
                <Typography className={classes.amcName}>{option.label}</Typography>
                {hasFee && (
                  <Typography className={classes.price}>
                    ${whole}
                    <span className={classes.priceCents}>.{cents}</span>
                  </Typography>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {hasFeeOptions && (
        <Box className={classes.disclaimer}>
          <InfoOutlined className={classes.disclaimerIcon} />
          <Typography className={classes.disclaimerText}>
            Estimated fees shown. Final pricing is determined by the AMC after order is placed and may vary based on property type, location, and complexity.
          </Typography>
        </Box>
      )}
    </Box>
  );
});
